import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import KnowledgeBaseFileManager from '../components/KnowledgeBaseFileManager';
import KnowledgeBaseAssignmentModule from '../modules/knowledge-base-assignment/KnowledgeBaseAssignmentModule.jsx';
import { useApi } from '../hooks/useApi';
import FileUploadModule from '../modules/file-upload/FileUploadModule.jsx';
import FileListModule from '../modules/file-list/FileListModule.jsx';
import FilterModule from '../modules/filter/FilterModule.jsx';
import TextFileEditorModule from '../modules/text-editor/TextFileEditorModule.jsx';
import { ModuleContextProvider } from '../modules/context/ModuleContextProvider.jsx';
import { useToast } from '../contexts/ToastContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { getThemeClasses, getButtonClasses, getCardClasses } from '../utils/themeUtils';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorBoundary from '../components/ErrorBoundary';
import IndexingStatusIndicator from '../components/IndexingStatusIndicator.jsx';

const KnowledgeBase = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { execute, apiClient } = useApi();
  const toast = useToast();
  const theme = useTheme();

  // Debug user context and dependencies - only log when dependencies change
  // (Removed render-time logging to prevent infinite loops)

  // State for managing view mode and files
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'files', 'assignments', 'sync-monitor'
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [syncStats, setSyncStats] = useState({
    total: 0,
    synced: 0,
    pending: 0,
    failed: 0,
    syncing: 0
  });
  const [parrotUsageData, setParrotUsageData] = useState([]);
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);

  // Define user permissions for file upload and knowledge base management
  const userPermissions = [
    { id: 'USER_KB_UPLOAD', name: 'Upload Knowledge Base Files' },
    { id: 'FILE_UPLOAD', name: 'Upload Files' },
    { id: 'KB_MANAGEMENT', name: 'Manage Knowledge Base' },
    { id: 'knowledge_base_read', name: 'Read Knowledge Base' },
    { id: 'knowledge_base_assign', name: 'Assign Knowledge Base Files' },
    { id: 'knowledge_base_batch_operations', name: 'Batch Operations' }
  ];

  const fetchKnowledgeFiles = useCallback(async () => {
    try {
      setLoadingFiles(true);
      await execute(async (api) => {
        const response = await api.get('/api/knowledge-base');
        // Handle API response format
        const files = response.data || response.documents || response || [];
        // Ensure files is always an array
        const filesArray = Array.isArray(files) ? files : [];
        setKnowledgeFiles(filesArray);
        
        // Calculate sync statistics
        const stats = filesArray.reduce((acc, file) => {
          acc.total++;
          switch (file.sync_status) {
            case 'synced':
              acc.synced++;
              break;
            case 'pending':
              acc.pending++;
              break;
            case 'failed':
            case 'error':
              acc.failed++;
              break;
            case 'syncing':
              acc.syncing++;
              break;
            default:
              acc.pending++;
          }
          return acc;
        }, { total: 0, synced: 0, pending: 0, failed: 0, syncing: 0 });
        
        setSyncStats(stats);
        
        return filesArray;
      }, { showErrorToast: true });
    } catch (error) {
      console.error('Failed to fetch knowledge base files:', error);
    } finally {
      setLoadingFiles(false);
    }
  }, [execute]);

  // Fetch parrot usage data
  const fetchParrotUsageData = useCallback(async () => {
    try {
      await execute(async (api) => {
        const response = await api.get('/api/knowledge-base/usage-stats');
        const usageData = response.data || response || [];
        setParrotUsageData(Array.isArray(usageData) ? usageData : []);
        return usageData;
      }, { showErrorToast: false });
    } catch (error) {
      console.error('Failed to fetch parrot usage data:', error);
    }
  }, [execute]);

  // Load knowledge base files and usage data
  useEffect(() => {
    if (isSignedIn) {
      fetchKnowledgeFiles();
      fetchParrotUsageData();
    }
  }, [isSignedIn, fetchKnowledgeFiles, fetchParrotUsageData]);

  // Real-time updates for sync status
  useEffect(() => {
    if (!realTimeUpdates || !isSignedIn) return;

    const interval = setInterval(() => {
      // Only refresh if there are files in syncing or pending state
      if (syncStats.syncing > 0 || syncStats.pending > 0) {
        fetchKnowledgeFiles();
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [realTimeUpdates, isSignedIn, syncStats.syncing, syncStats.pending, fetchKnowledgeFiles]);

  const handleAssignmentChange = useCallback(() => {
    // Refresh files when assignments change
    fetchKnowledgeFiles();
  }, [fetchKnowledgeFiles]);

  // File management handlers
  const handleFileEdit = useCallback((fileId) => {
    const file = knowledgeFiles.find(f => f.id === fileId);
    if (file && (file.type === 'text' || file.mime_type === 'text/plain')) {
      setEditingFile(file);
      setShowTextEditor(true);
    }
  }, [knowledgeFiles]);

  const handleFileDelete = useCallback(async (fileId) => {
    try {
      await execute(async (api) => {
        await api.delete(`/api/knowledge-base/${fileId}`);
        // Invalidate caches before refetch to avoid stale list
        api.invalidateCaches([
          '/api/knowledge-base',
          '/api/knowledge-base/files',
          '/api/knowledge-base/usage-stats'
        ]);
        // Refresh files after deletion
        await fetchKnowledgeFiles();
        await fetchParrotUsageData();
        return true;
      }, { showErrorToast: true });
      
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    }
  }, [execute, fetchKnowledgeFiles, fetchParrotUsageData, toast]);

  const handleSyncRetry = useCallback(async (fileId) => {
    try {
      await execute(async (api) => {
        await api.post(`/api/knowledge-base/${fileId}/retry-sync`);
        // Refresh files after retry
        await fetchKnowledgeFiles();
        return true;
      }, { showErrorToast: true });
      
      toast.success('Sync retry initiated');
    } catch (error) {
      console.error('Failed to retry sync:', error);
      toast.error('Failed to retry sync');
    }
  }, [execute, fetchKnowledgeFiles, toast]);

  const handleBulkOperation = useCallback(async (operation, fileIds) => {
    try {
      await execute(async (api) => {
        switch (operation) {
          case 'delete':
            await Promise.all(fileIds.map(id => api.delete(`/api/knowledge-base/${id}`)));
            toast.success(`Deleted ${fileIds.length} files`);
            break;
          case 'retry-sync':
            await Promise.all(fileIds.map(id => api.post(`/api/knowledge-base/${id}/retry-sync`)));
            toast.success(`Retry initiated for ${fileIds.length} files`);
            break;
          default:
            throw new Error(`Unknown operation: ${operation}`);
        }
        // Invalidate caches before refetch
        api.invalidateCaches([
          '/api/knowledge-base',
          '/api/knowledge-base/files',
          '/api/knowledge-base/usage-stats'
        ]);
        // Refresh data after bulk operation
        await fetchKnowledgeFiles();
        await fetchParrotUsageData();
        return true;
      }, { showErrorToast: true });
    } catch (error) {
      console.error(`Failed to perform bulk ${operation}:`, error);
      toast.error(`Failed to perform bulk ${operation}`);
    }
  }, [execute, fetchKnowledgeFiles, fetchParrotUsageData, toast]);

  // Text editor handlers
  const handleTextFileSave = useCallback(async (filename, content, metadata) => {
    try {
      await execute(async (api) => {
        const endpoint = editingFile 
          ? `/api/knowledge-base/${editingFile.id}` 
          : '/api/knowledge-base/text';
        
        const method = editingFile ? 'put' : 'post';
        
        await api[method](endpoint, {
          filename,
          content,
          metadata
        });
        
        // Refresh files after save
        await fetchKnowledgeFiles();
        return true;
      }, { showErrorToast: true });
      
      setShowTextEditor(false);
      setEditingFile(null);
      toast.success(`Text file ${editingFile ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Failed to save text file:', error);
      throw error; // Re-throw to let TextFileEditorModule handle it
    }
  }, [execute, fetchKnowledgeFiles, editingFile, toast]);

  const handleTextEditorCancel = useCallback(() => {
    setShowTextEditor(false);
    setEditingFile(null);
  }, []);

  // Export/Import handlers
  const handleExportKnowledgeBase = useCallback(async () => {
    try {
      await execute(async (api) => {
        const response = await api.get('/api/knowledge-base/export');
        const exportData = response.data || response;
        
        // Create and download file
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `knowledge-base-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        return true;
      }, { showErrorToast: true });
      
      toast.success('Knowledge base exported successfully');
    } catch (error) {
      console.error('Failed to export knowledge base:', error);
      toast.error('Failed to export knowledge base');
    }
  }, [execute, toast]);

  const handleImportKnowledgeBase = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('backup', file);
      
      await execute(async (api) => {
        await api.post('/api/knowledge-base/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Refresh data after import
        await fetchKnowledgeFiles();
        await fetchParrotUsageData();
        return true;
      }, { showErrorToast: true });
      
      toast.success('Knowledge base imported successfully');
    } catch (error) {
      console.error('Failed to import knowledge base:', error);
      toast.error('Failed to import knowledge base');
    }
  }, [execute, fetchKnowledgeFiles, fetchParrotUsageData, toast]);

  // Filter configuration for knowledge base context
  const filterConfig = useMemo(() => ({
    context: 'knowledge-base',
    filters: [
      {
        id: 'file_type',
        label: 'File Type',
        type: 'select',
        options: [
          { value: 'pdf', label: 'PDF' },
          { value: 'docx', label: 'DOCX' },
          { value: 'txt', label: 'TXT' },
          { value: 'html', label: 'HTML' },
          { value: 'text', label: 'Text Files' }
        ]
      },
      {
        id: 'sync_status',
        label: 'Sync Status',
        type: 'select',
        options: [
          { value: 'synced', label: 'Synced' },
          { value: 'pending', label: 'Pending' },
          { value: 'syncing', label: 'Syncing' },
          { value: 'failed', label: 'Failed' },
          { value: 'error', label: 'Error' }
        ]
      },
      {
        id: 'date_range',
        label: 'Created Date',
        type: 'date-range'
      },
      {
        id: 'search',
        label: 'Search',
        type: 'text',
        placeholder: 'Search file names...'
      }
    ],
    realTimeFiltering: true,
    persistFilters: true,
    storageKey: 'knowledge-base-filters',
    showClearAll: true,
    showExport: true,
    showImport: true,
    onFilterChange: (filters) => {
      // Apply filters to file list
      // This will be handled by the FilterModule
    }
  }), []);

  if (!isLoaded || !userLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading authentication...</p>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-600 font-mono dark:text-gray-300">
            Debug: isLoaded={String(isLoaded)}, userLoaded={String(userLoaded)}
          </div>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Please sign in to access knowledge base management.</p>
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-600 font-mono dark:text-gray-300">
            Debug: isLoaded={String(isLoaded)}, isSignedIn=false
          </div>
        </div>
      </div>
    );
  }

  // Show loading if user is not available yet (only if signed in but user not loaded)
  if (isSignedIn && !user) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading user profile...</p>
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-600 font-mono dark:text-gray-300">
            Debug: isLoaded={String(isLoaded)}, userLoaded={String(userLoaded)}, isSignedIn={String(isSignedIn)}, user={user ? 'exists' : 'null'}
          </div>
        </div>
      </div>
    );
  }

  // If we're loaded and signed in but no user object, show debug info
  if (!user && isLoaded && isSignedIn) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Authentication Issue</h3>
            <p className="text-yellow-600 dark:text-yellow-400 mb-4">
              There seems to be an issue with user authentication. Please try refreshing the page.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg dark:text-white"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Removed render-time logging to prevent infinite loops

  return (
    <div>
      {/* Debug Panel - only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border dark:bg-gray-700">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">Debug Info</h4>
          <div className="text-xs font-mono text-gray-600 dark:text-gray-400 space-y-1 dark:text-gray-300 dark:text-gray-600">
            <div>isLoaded: {String(isLoaded)}</div>
            <div>userLoaded: {String(userLoaded)}</div>
            <div>isSignedIn: {String(isSignedIn)}</div>
            <div>user: {user ? `exists (id: ${user.id || user.userId || user.sub || 'no-id'})` : 'null'}</div>
            <div>apiClient: {apiClient ? 'exists' : 'null'}</div>
            <div>execute: {execute ? 'exists' : 'null'}</div>
            <div>viewMode: {viewMode}</div>
            <div>loadingFiles: {String(loadingFiles)}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Knowledge Base</h1>
            <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
              Comprehensive knowledge base management with file upload, sync monitoring, and parrot assignments.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTextEditor(true)}
              className={getButtonClasses('secondary', 'medium')}
            >
              Create Text File
            </button>
            <button
              onClick={handleExportKnowledgeBase}
              className={getButtonClasses('secondary', 'medium')}
            >
              Export Backup
            </button>
            <label className={getButtonClasses('secondary', 'medium')}>
              Import Backup
              <input
                type="file"
                accept=".json"
                onChange={(e) => e.target.files[0] && handleImportKnowledgeBase(e.target.files[0])}
                className="hidden"
              />
            </label>
            <button
              onClick={() => setRealTimeUpdates(!realTimeUpdates)}
              className={getButtonClasses(realTimeUpdates ? 'primary' : 'secondary', 'medium')}
              title={realTimeUpdates ? 'Disable real-time updates' : 'Enable real-time updates'}
            >
              {realTimeUpdates ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="mt-6">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 max-w-fit">
            <button
              onClick={() => setViewMode('dashboard')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'dashboard'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setViewMode('files')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'files'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              File Management
            </button>
            <button
              onClick={() => setViewMode('assignments')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'assignments'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Parrot Assignments
            </button>
            <button
              onClick={() => setViewMode('sync-monitor')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'sync-monitor'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Sync Monitor
              {(syncStats.syncing > 0 || syncStats.failed > 0) && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  {syncStats.syncing + syncStats.failed}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Text File Editor Modal */}
      {showTextEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {editingFile ? `Edit ${editingFile.name}` : 'Create New Text File'}
              </h2>
              <ModuleContextProvider
                user={user}
                permissions={userPermissions}
                apiClient={apiClient}
                theme={theme}
                toast={toast}
                config={{
                  debugMode: false,
                  maxEventHistory: 100
                }}
              >
                <TextFileEditorModule
                  config={{
                    mode: editingFile ? 'edit' : 'create',
                    initialContent: editingFile?.content || '',
                    initialFilename: editingFile?.name?.replace('.txt', '') || '',
                    maxLength: 50000,
                    autoSave: true,
                    showCharacterCount: true,
                    showWordCount: true,
                    enableMarkdown: true,
                    enablePreview: true,
                    enableKeyboardShortcuts: true,
                    saveEndpoint: '/api/modules/text-editor/save',
                    onSave: handleTextFileSave,
                    onCancel: handleTextEditorCancel
                  }}
                />
              </ModuleContextProvider>
            </div>
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      <ModuleContextProvider
        user={user}
        permissions={userPermissions}
        apiClient={apiClient}
        theme={theme}
        toast={toast}
        config={{
          debugMode: false,
          maxEventHistory: 100
        }}
      >
        {loadingFiles ? (
          <div className="flex items-center justify-center py-12">
            <LoadingIndicator type="spinner" size="large" />
          </div>
        ) : (
          <>
            {viewMode === 'dashboard' && (
              <DashboardView 
                syncStats={syncStats}
                knowledgeFiles={knowledgeFiles}
                parrotUsageData={parrotUsageData}
                onFileEdit={handleFileEdit}
                onFileDelete={handleFileDelete}
                onSyncRetry={handleSyncRetry}
                onBulkOperation={handleBulkOperation}
                onRefresh={fetchKnowledgeFiles}
              />
            )}

            {viewMode === 'files' && (
              <FileManagementView
                knowledgeFiles={knowledgeFiles}
                onFileEdit={handleFileEdit}
                onFileDelete={handleFileDelete}
                onSyncRetry={handleSyncRetry}
                onBulkOperation={handleBulkOperation}
                onFilesChange={fetchKnowledgeFiles}
                filterConfig={filterConfig}
              />
            )}

            {viewMode === 'assignments' && (
              <AssignmentView
                knowledgeFiles={knowledgeFiles}
                onAssignmentChange={handleAssignmentChange}
              />
            )}

            {viewMode === 'sync-monitor' && (
              <SyncMonitorView
                knowledgeFiles={knowledgeFiles}
                syncStats={syncStats}
                realTimeUpdates={realTimeUpdates}
                onSyncRetry={handleSyncRetry}
                onBulkOperation={handleBulkOperation}
                onRefresh={fetchKnowledgeFiles}
              />
            )}
          </>
        )}
      </ModuleContextProvider>
    </div>
  );
};

// Dashboard View Component - Overview with stats and recent activity
const DashboardView = ({ 
  syncStats, 
  knowledgeFiles, 
  parrotUsageData, 
  onFileEdit, 
  onFileDelete, 
  onSyncRetry, 
  onBulkOperation,
  onRefresh 
}) => {
  const theme = getThemeClasses();
  
  // Recent files (last 5)
  const recentFiles = knowledgeFiles
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);
  
  // Files needing attention (failed sync)
  const failedFiles = knowledgeFiles.filter(f =>
    f.sync_status === 'failed' || f.sync_status === 'error'
  );

  // Files with active indexing operations
  const activeIndexingFiles = knowledgeFiles.filter(f =>
    f.indexing_status && ['indexing', 'retrying', 'queued', 'pending'].includes(f.indexing_status)
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${theme.text.secondary}`}>Total Files</p>
                <p className={`text-2xl font-semibold ${theme.text.primary}`}>{syncStats.total}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${theme.text.secondary}`}>Synced</p>
                <p className={`text-2xl font-semibold ${theme.text.primary}`}>{syncStats.synced}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${theme.text.secondary}`}>Pending</p>
                <p className={`text-2xl font-semibold ${theme.text.primary}`}>{syncStats.pending + syncStats.syncing}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${theme.text.secondary}`}>Failed</p>
                <p className={`text-2xl font-semibold ${theme.text.primary}`}>{syncStats.failed}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Vector Indexing */}
      {activeIndexingFiles.length > 0 && (
        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-medium ${theme.text.primary}`}>
                🧠 Active Vector Indexing ({activeIndexingFiles.length})
              </h3>
              <button
                onClick={onRefresh}
                className={getButtonClasses('secondary', 'small')}
              >
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeIndexingFiles.slice(0, 6).map(file => (
                <IndexingStatusIndicator
                  key={file.id}
                  fileId={file.id}
                  fileName={file.name}
                  initialStatus={file.indexing_status}
                  initialProgress={file.indexing_progress || 0}
                  showRetry={true}
                  compact={true}
                  onStatusChange={(newStatus, progress) => {
                    // Update the file in the local state to reflect status change
                    onRefresh();
                  }}
                />
              ))}
            </div>

            {activeIndexingFiles.length > 6 && (
              <div className={`text-center mt-4 ${theme.text.muted}`}>
                +{activeIndexingFiles.length - 6} more files being indexed...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Parrot Assignment Overview */}
      <ParrotAssignmentOverview
        knowledgeFiles={knowledgeFiles}
        parrotUsageData={parrotUsageData}
        onRefresh={onRefresh}
      />

      {/* Quick Actions */}
      <div className={getCardClasses()}>
        <div className="p-6">
          <h3 className={`text-lg font-medium ${theme.text.primary} mb-4`}>Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <FileUploadModule
              config={{
                context: 'user-kb-dashboard',
                securityLevel: 'user',
                allowedTypes: ['pdf', 'docx', 'txt', 'html'],
                maxFileSize: 4 * 1024 * 1024,
                multiple: true,
                showProgress: true,
                concurrent: false,
                triggerSync: false,
                uploadEndpoint: '/api/modules/file-upload',
                onUploadComplete: onRefresh,
                compact: true
              }}
            />
            {failedFiles.length > 0 && (
              <button
                onClick={() => onBulkOperation('retry-sync', failedFiles.map(f => f.id))}
                className={getButtonClasses('secondary', 'medium')}
              >
                Retry All Failed ({failedFiles.length})
              </button>
            )}
            <button
              onClick={onRefresh}
              className={getButtonClasses('secondary', 'medium')}
            >
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Recent Files and Failed Files */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Files */}
        <div className={getCardClasses()}>
          <div className="p-6">
            <h3 className={`text-lg font-medium ${theme.text.primary} mb-4`}>Recent Files</h3>
            {recentFiles.length > 0 ? (
              <FileListModule
                files={recentFiles}
                onFileEdit={onFileEdit}
                onFileDelete={onFileDelete}
                onSyncRetry={onSyncRetry}
                showSyncStatus={true}
                allowSelection={false}
                showUsage={true}
                allowEdit={true}
                context="dashboard"
                emptyMessage="No recent files"
              />
            ) : (
              <p className={theme.text.muted}>No files yet</p>
            )}
          </div>
        </div>

        {/* Files Needing Attention */}
        <div className={getCardClasses()}>
          <div className="p-6">
            <h3 className={`text-lg font-medium ${theme.text.primary} mb-4`}>
              Files Needing Attention
              {failedFiles.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  {failedFiles.length}
                </span>
              )}
            </h3>
            {failedFiles.length > 0 ? (
              <FileListModule
                files={failedFiles}
                onFileEdit={onFileEdit}
                onFileDelete={onFileDelete}
                onSyncRetry={onSyncRetry}
                showSyncStatus={true}
                allowSelection={false}
                showUsage={true}
                allowEdit={true}
                context="dashboard"
                emptyMessage="No files need attention"
              />
            ) : (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className={`mt-2 ${theme.text.muted}`}>All files are synced successfully!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parrot Usage Summary */}
      {parrotUsageData.length > 0 && (
        <div className={getCardClasses()}>
          <div className="p-6">
            <h3 className={`text-lg font-medium ${theme.text.primary} mb-4`}>Parrot Usage Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {parrotUsageData.slice(0, 6).map((usage, index) => (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className={`font-medium ${theme.text.primary}`}>{usage.parrotName}</p>
                  <p className={`text-sm ${theme.text.secondary}`}>
                    {usage.fileCount} files • {usage.totalSize} total
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// File Management View Component - Enhanced file management with filtering
const FileManagementView = ({ 
  knowledgeFiles, 
  onFileEdit, 
  onFileDelete, 
  onSyncRetry, 
  onBulkOperation, 
  onFilesChange,
  filterConfig 
}) => {
  const [filteredFiles, setFilteredFiles] = useState(knowledgeFiles);

  const handleFilterChange = useCallback((filters) => {
    let filtered = [...knowledgeFiles];
    
    filters.forEach(filter => {
      switch (filter.id) {
        case 'file_type':
          filtered = filtered.filter(f => f.type === filter.value || f.mime_type?.includes(filter.value));
          break;
        case 'sync_status':
          filtered = filtered.filter(f => f.sync_status === filter.value);
          break;
        case 'search':
          filtered = filtered.filter(f => 
            f.name.toLowerCase().includes(filter.value.toLowerCase())
          );
          break;
        case 'date_range':
          if (filter.value.start) {
            filtered = filtered.filter(f => 
              new Date(f.created_at) >= new Date(filter.value.start)
            );
          }
          if (filter.value.end) {
            filtered = filtered.filter(f => 
              new Date(f.created_at) <= new Date(filter.value.end)
            );
          }
          break;
      }
    });
    
    setFilteredFiles(filtered);
  }, [knowledgeFiles]);

  useEffect(() => {
    setFilteredFiles(knowledgeFiles);
  }, [knowledgeFiles]);

  return (
    <div className="space-y-6">
      {/* File Upload */}
      <FileUploadModule
        config={{
          context: 'user-kb-files',
          securityLevel: 'user',
          allowedTypes: ['pdf', 'docx', 'txt', 'html'],
          maxFileSize: 4 * 1024 * 1024,
          multiple: true,
          showProgress: true,
          concurrent: false,
          triggerSync: false,
          uploadEndpoint: '/api/modules/file-upload',
          onUploadComplete: onFilesChange
        }}
      />

      {/* Filters */}
      <FilterModule
        config={{
          ...filterConfig,
          onFilterChange: handleFilterChange
        }}
      />

      {/* File List */}
      <FileListModule
        files={filteredFiles}
        onFileEdit={onFileEdit}
        onFileDelete={onFileDelete}
        onSyncRetry={onSyncRetry}
        onBulkOperation={onBulkOperation}
        showSyncStatus={true}
        allowSelection={true}
        showUsage={true}
        allowEdit={true}
        allowBulkOperations={true}
        context="file-management"
      />
    </div>
  );
};

// Assignment View Component - Parrot assignments
const AssignmentView = ({ knowledgeFiles, onAssignmentChange }) => {
  const { execute } = useApi();
  const toast = useToast();
  const [parrots, setParrots] = useState([]);
  const [loadingParrots, setLoadingParrots] = useState(true);
  const [showQuickAssign, setShowQuickAssign] = useState(false);

  // Load parrots for assignment
  useEffect(() => {
    const loadParrots = async () => {
      try {
        setLoadingParrots(true);
        await execute(async (api) => {
          const response = await api.get('/api/knowledge-base/parrots');
          setParrots(response.parrots || []);
          return response;
        }, { showErrorToast: false });
      } catch (error) {
        console.error('Failed to load parrots:', error);
      } finally {
        setLoadingParrots(false);
      }
    };

    loadParrots();
  }, [execute]);

  // Get assignment statistics
  const assignmentStats = useMemo(() => {
    const stats = {
      totalFiles: knowledgeFiles.length,
      assignedFiles: 0,
      unassignedFiles: 0,
      totalAssignments: 0,
      parrotsWithFiles: new Set()
    };

    knowledgeFiles.forEach(file => {
      if (file.assignments && file.assignments.length > 0) {
        stats.assignedFiles++;
        stats.totalAssignments += file.assignments.length;
        file.assignments.forEach(assignment => {
          stats.parrotsWithFiles.add(assignment.parrotId);
        });
      } else {
        stats.unassignedFiles++;
      }
    });

    stats.parrotsWithFiles = stats.parrotsWithFiles.size;
    return stats;
  }, [knowledgeFiles]);

  return (
    <div className="space-y-6">
      {/* Assignment Statistics */}
      <div className={getCardClasses()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Assignment Overview
            </h3>
            <button
              onClick={() => setShowQuickAssign(!showQuickAssign)}
              className={getButtonClasses('primary', 'medium')}
            >
              {showQuickAssign ? 'Hide' : 'Show'} Quick Assign
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {assignmentStats.totalFiles}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Files</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {assignmentStats.assignedFiles}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Assigned Files</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {assignmentStats.unassignedFiles}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Unassigned Files</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {assignmentStats.parrotsWithFiles}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Parrots with Files</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Assignment Panel */}
      {showQuickAssign && (
        <QuickAssignmentPanel
          knowledgeFiles={knowledgeFiles}
          parrots={parrots}
          loadingParrots={loadingParrots}
          onAssignmentChange={onAssignmentChange}
        />
      )}

      {/* Main Assignment Interface */}
      <KnowledgeBaseAssignmentModule
        config={{
          context: 'user-kb-dashboard',
          mode: 'multi-select',
          securityLevel: 'user',
          showSyncStatus: true,
          allowBatchOperations: true,
          enableRealTimeUpdates: true,
          onAssignmentChange: onAssignmentChange
        }}
        files={knowledgeFiles}
      />
    </div>
  );
};

// Sync Monitor View Component - Real-time sync monitoring
const SyncMonitorView = ({
  knowledgeFiles,
  syncStats,
  realTimeUpdates,
  onSyncRetry,
  onBulkOperation,
  onRefresh
}) => {
  const theme = getThemeClasses();

  // Group files by sync status
  const filesByStatus = {
    syncing: knowledgeFiles.filter(f => f.sync_status === 'syncing'),
    pending: knowledgeFiles.filter(f => f.sync_status === 'pending'),
    failed: knowledgeFiles.filter(f => f.sync_status === 'failed' || f.sync_status === 'error'),
    synced: knowledgeFiles.filter(f => f.sync_status === 'synced')
  };

  // Get files with indexing status (show all files that have indexing-related status)
  const filesWithIndexingStatus = knowledgeFiles.filter(f =>
    f.indexing_status &&
    ['pending', 'queued', 'indexing', 'retrying', 'failed'].includes(f.indexing_status)
  );

  return (
    <div className="space-y-6">
      {/* Sync Status Overview */}
      <div className={getCardClasses()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-medium ${theme.text.primary}`}>Sync Status Overview</h3>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${theme.text.secondary}`}>
                Real-time updates: {realTimeUpdates ? 'ON' : 'OFF'}
              </span>
              <button
                onClick={onRefresh}
                className={getButtonClasses('secondary', 'small')}
              >
                Refresh
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className={theme.text.secondary}>Sync Progress</span>
              <span className={theme.text.secondary}>
                {syncStats.synced} of {syncStats.total} synced
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: syncStats.total > 0 ? `${(syncStats.synced / syncStats.total) * 100}%` : '0%' 
                }}
              />
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{syncStats.syncing}</div>
              <div className={`text-sm ${theme.text.secondary}`}>Syncing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{syncStats.pending}</div>
              <div className={`text-sm ${theme.text.secondary}`}>Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{syncStats.failed}</div>
              <div className={`text-sm ${theme.text.secondary}`}>Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{syncStats.synced}</div>
              <div className={`text-sm ${theme.text.secondary}`}>Synced</div>
            </div>
          </div>
        </div>
      </div>

      {/* Vector Index Status */}
      {filesWithIndexingStatus.length > 0 && (
        <div className={getCardClasses()}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-medium ${theme.text.primary}`}>
                🧠 Vector Index Status ({filesWithIndexingStatus.length})
              </h3>
              <button
                onClick={onRefresh}
                className={getButtonClasses('secondary', 'small')}
              >
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filesWithIndexingStatus.map(file => (
                <IndexingStatusIndicator
                  key={file.id}
                  fileId={file.id}
                  fileName={file.name}
                  initialStatus={file.indexing_status}
                  initialProgress={file.indexing_progress || 0}
                  showRetry={true}
                  compact={true}
                  onStatusChange={(newStatus, progress) => {
                    // Update the file in the local state to reflect status change
                    onRefresh();
                  }}
                />
              ))}
            </div>

            {filesWithIndexingStatus.length === 0 && (
              <div className="text-center py-4">
                <p className={theme.text.muted}>No files currently being indexed</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Files by Status */}
      {Object.entries(filesByStatus).map(([status, files]) => {
        if (files.length === 0) return null;
        
        const statusConfig = {
          syncing: { title: 'Currently Syncing', color: 'blue', icon: '⟳' },
          pending: { title: 'Pending Sync', color: 'yellow', icon: '⏳' },
          failed: { title: 'Failed Sync', color: 'red', icon: '⚠️' },
          synced: { title: 'Successfully Synced', color: 'green', icon: '✅' }
        };

        const config = statusConfig[status];
        
        return (
          <div key={status} className={getCardClasses()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${theme.text.primary}`}>
                  {config.icon} {config.title} ({files.length})
                </h3>
                {status === 'failed' && files.length > 0 && (
                  <button
                    onClick={() => onBulkOperation('retry-sync', files.map(f => f.id))}
                    className={getButtonClasses('secondary', 'small')}
                  >
                    Retry All
                  </button>
                )}
              </div>
              
              <FileListModule
                files={files}
                onSyncRetry={onSyncRetry}
                showSyncStatus={true}
                allowSelection={status === 'failed'}
                showUsage={false}
                allowEdit={false}
                allowBulkOperations={status === 'failed'}
                context="sync-monitor"
                emptyMessage={`No ${status} files`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Parrot Assignment Overview Component - Shows which files are assigned to which parrots
const ParrotAssignmentOverview = ({ knowledgeFiles, parrotUsageData, onRefresh }) => {
  const theme = getThemeClasses();
  const { execute } = useApi();
  const toast = useToast();

  // Group files by parrot assignments
  const assignmentData = useMemo(() => {
    const parrotMap = new Map();
    
    knowledgeFiles.forEach(file => {
      if (file.assignments && file.assignments.length > 0) {
        file.assignments.forEach(assignment => {
          if (!parrotMap.has(assignment.parrotId)) {
            parrotMap.set(assignment.parrotId, {
              parrotId: assignment.parrotId,
              parrotName: assignment.parrotName,
              files: [],
              syncStats: { synced: 0, pending: 0, failed: 0, syncing: 0 }
            });
          }
          
          const parrotData = parrotMap.get(assignment.parrotId);
          parrotData.files.push({
            ...file,
            assignedAt: assignment.assignedAt
          });
          
          // Update sync stats
          switch (file.sync_status) {
            case 'synced':
              parrotData.syncStats.synced++;
              break;
            case 'pending':
              parrotData.syncStats.pending++;
              break;
            case 'syncing':
              parrotData.syncStats.syncing++;
              break;
            case 'failed':
            case 'error':
              parrotData.syncStats.failed++;
              break;
          }
        });
      }
    });
    
    return Array.from(parrotMap.values()).sort((a, b) => a.parrotName.localeCompare(b.parrotName));
  }, [knowledgeFiles]);

  // Handle quick unassign
  const handleQuickUnassign = async (fileId, parrotId, fileName, parrotName) => {
    try {
      await execute(async (api) => {
        await api.post('/api/knowledge-base/assignments', {
          fileIds: [fileId],
          parrotIds: [parrotId],
          operation: 'unassign'
        });
        return true;
      }, { showErrorToast: true });
      
      toast.success(`Unassigned "${fileName}" from "${parrotName}"`);
      onRefresh();
    } catch (error) {
      console.error('Failed to unassign file:', error);
      toast.error('Failed to unassign file');
    }
  };

  if (assignmentData.length === 0) {
    return (
      <div className={getCardClasses()}>
        <div className="p-6 text-center">
          <div className="text-6xl mb-4">🦜</div>
          <h3 className={`text-lg font-medium ${theme.text.primary} mb-2`}>
            No Parrot Assignments
          </h3>
          <p className={theme.text.muted}>
            Assign knowledge base files to your parrots to enhance their responses.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={getCardClasses()}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-medium ${theme.text.primary}`}>
            Parrot Assignments ({assignmentData.length} parrots)
          </h3>
          <button
            onClick={onRefresh}
            className={getButtonClasses('secondary', 'small')}
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assignmentData.map(parrot => (
            <div
              key={parrot.parrotId}
              className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
            >
              {/* Parrot header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🦜</span>
                  <div>
                    <h4 className={`font-medium ${theme.text.primary}`}>
                      {parrot.parrotName}
                    </h4>
                    <p className={`text-sm ${theme.text.muted}`}>
                      {parrot.files.length} file{parrot.files.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sync status summary */}
              <div className="flex items-center space-x-2 mb-3">
                {parrot.syncStats.synced > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                    ✅ {parrot.syncStats.synced}
                  </span>
                )}
                {parrot.syncStats.syncing > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                    🔄 {parrot.syncStats.syncing}
                  </span>
                )}
                {parrot.syncStats.pending > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                    ⏳ {parrot.syncStats.pending}
                  </span>
                )}
                {parrot.syncStats.failed > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                    ❌ {parrot.syncStats.failed}
                  </span>
                )}
              </div>

              {/* Files list */}
              <div className="space-y-2">
                {parrot.files.slice(0, 3).map(file => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <span className="text-sm">
                        {file.type === 'file' && file.mime_type?.includes('pdf') && '📄'}
                        {file.type === 'file' && file.mime_type?.includes('text') && '📝'}
                        {file.type === 'file' && file.mime_type?.includes('word') && '📄'}
                        {file.type === 'file' && file.mime_type?.includes('html') && '🌐'}
                        {file.type === 'url' && '🔗'}
                        {file.type === 'text' && '📝'}
                        {!file.type && '📄'}
                      </span>
                      <span className={`text-sm truncate ${theme.text.primary}`}>
                        {file.name}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          file.sync_status === 'synced' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                          file.sync_status === 'syncing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
                          file.sync_status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
                          file.sync_status === 'failed' || file.sync_status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {file.sync_status === 'synced' && '✅'}
                        {file.sync_status === 'syncing' && '🔄'}
                        {file.sync_status === 'pending' && '⏳'}
                        {(file.sync_status === 'failed' || file.sync_status === 'error') && '❌'}
                        {!file.sync_status && '❓'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleQuickUnassign(file.id, parrot.parrotId, file.name, parrot.parrotName)}
                      className="ml-2 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title={`Unassign "${file.name}" from "${parrot.parrotName}"`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                
                {parrot.files.length > 3 && (
                  <div className={`text-sm ${theme.text.muted} text-center py-1`}>
                    +{parrot.files.length - 3} more files
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Quick Assignment Panel Component - Allows quick bulk assignments
const QuickAssignmentPanel = ({ knowledgeFiles, parrots, loadingParrots, onAssignmentChange }) => {
  const { execute } = useApi();
  const toast = useToast();
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectedParrots, setSelectedParrots] = useState(new Set());
  const [operation, setOperation] = useState('assign');
  const [processing, setProcessing] = useState(false);

  // Get unassigned files
  const unassignedFiles = knowledgeFiles.filter(file => 
    !file.assignments || file.assignments.length === 0
  );

  // Handle bulk assignment
  const handleBulkAssignment = async () => {
    if (selectedFiles.size === 0 || selectedParrots.size === 0) {
      toast.error('Please select at least one file and one parrot');
      return;
    }

    try {
      setProcessing(true);
      await execute(async (api) => {
        const response = await api.post('/api/knowledge-base/assignments', {
          fileIds: Array.from(selectedFiles),
          parrotIds: Array.from(selectedParrots),
          operation: operation
        });

        if (response.success) {
          const summary = response.summary || {};
          toast.success(
            `Bulk ${operation} completed: ${summary.successful || 0} successful, ${summary.skipped || 0} skipped, ${summary.errors || 0} errors`
          );
          
          // Clear selections
          setSelectedFiles(new Set());
          setSelectedParrots(new Set());
          
          // Refresh data
          onAssignmentChange();
        } else {
          throw new Error(response.error?.message || 'Bulk operation failed');
        }

        return response;
      }, { showErrorToast: true });
    } catch (error) {
      console.error('Bulk assignment error:', error);
      toast.error(`Bulk ${operation} failed: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Handle file selection
  const handleFileSelection = (fileId, selected) => {
    const newSelection = new Set(selectedFiles);
    if (selected) {
      newSelection.add(fileId);
    } else {
      newSelection.delete(fileId);
    }
    setSelectedFiles(newSelection);
  };

  // Handle parrot selection
  const handleParrotSelection = (parrotId, selected) => {
    const newSelection = new Set(selectedParrots);
    if (selected) {
      newSelection.add(parrotId);
    } else {
      newSelection.delete(parrotId);
    }
    setSelectedParrots(newSelection);
  };

  // Select all unassigned files
  const selectAllUnassigned = () => {
    setSelectedFiles(new Set(unassignedFiles.map(f => f.id)));
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedFiles(new Set());
    setSelectedParrots(new Set());
  };

  return (
    <div className={getCardClasses()}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Quick Assignment Panel
          </h3>
          <div className="flex items-center space-x-2">
            <select
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="assign">Assign</option>
              <option value="unassign">Unassign</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* File Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Select Files ({selectedFiles.size} selected)
              </h4>
              <div className="flex items-center space-x-2">
                <button
                  onClick={selectAllUnassigned}
                  className={getButtonClasses('secondary', 'small')}
                  disabled={unassignedFiles.length === 0}
                >
                  Select Unassigned ({unassignedFiles.length})
                </button>
                <button
                  onClick={() => setSelectedFiles(new Set())}
                  className={getButtonClasses('secondary', 'small')}
                  disabled={selectedFiles.size === 0}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
              {knowledgeFiles.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No files available
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                  {knowledgeFiles.map(file => (
                    <label
                      key={file.id}
                      className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.id)}
                        onChange={(e) => handleFileSelection(file.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500"
                      />
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm">
                            {file.type === 'file' && file.mime_type?.includes('pdf') && '📄'}
                            {file.type === 'file' && file.mime_type?.includes('text') && '📝'}
                            {file.type === 'file' && file.mime_type?.includes('word') && '📄'}
                            {file.type === 'file' && file.mime_type?.includes('html') && '🌐'}
                            {file.type === 'url' && '🔗'}
                            {file.type === 'text' && '📝'}
                            {!file.type && '📄'}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {file.name}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{file.assignments?.length || 0} assignments</span>
                          <span>•</span>
                          <span className={
                            file.sync_status === 'synced' ? 'text-green-600 dark:text-green-400' :
                            file.sync_status === 'syncing' ? 'text-blue-600 dark:text-blue-400' :
                            file.sync_status === 'pending' ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-red-600 dark:text-red-400'
                          }>
                            {file.sync_status || 'unknown'}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Parrot Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Select Parrots ({selectedParrots.size} selected)
              </h4>
              <button
                onClick={() => setSelectedParrots(new Set())}
                className={getButtonClasses('secondary', 'small')}
                disabled={selectedParrots.size === 0}
              >
                Clear
              </button>
            </div>
            
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
              {loadingParrots ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading parrots...</span>
                </div>
              ) : parrots.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No parrots available
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                  {parrots.map(parrot => (
                    <label
                      key={parrot.id}
                      className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedParrots.has(parrot.id)}
                        onChange={(e) => handleParrotSelection(parrot.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">🦜</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {parrot.name}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {parrot.id}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedFiles.size > 0 && selectedParrots.size > 0 && (
              <span>
                Ready to {operation} {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} 
                {operation === 'assign' ? ' to ' : ' from '} 
                {selectedParrots.size} parrot{selectedParrots.size !== 1 ? 's' : ''}
                ({selectedFiles.size * selectedParrots.size} operations)
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={clearSelections}
              className={getButtonClasses('secondary', 'medium')}
              disabled={selectedFiles.size === 0 && selectedParrots.size === 0}
            >
              Clear All
            </button>
            <button
              onClick={handleBulkAssignment}
              className={getButtonClasses('primary', 'medium')}
              disabled={selectedFiles.size === 0 || selectedParrots.size === 0 || processing}
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                `${operation === 'assign' ? 'Assign' : 'Unassign'} Selected`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
